import {
  AnalyticsEventIdMessage,
  MessageFromParent,
  MessageFromParentType,
  MessageToParent,
  MessageToParentType,
} from '@stripe-internal/support-conversations-controller';

import {devLog} from '../util/dev';
import {isIframed} from '../util/isIframed';
import {
  NoMessageBus,
  ParentMessageBus,
  ParentMessageListenerFn,
} from './ParentMessageBus';
import {readMessageFromParent} from './readMessageFromParent';

export function createIframeMessageBus({
  audience,
  entryPoint,
  parentOrigin,
}: {
  audience?: string;
  entryPoint?: string;
  parentOrigin: string;
}): ParentMessageBus {
  if (!isIframed()) {
    return NoMessageBus;
  }

  const listeners: ParentMessageListenerFn[] = [];

  function handler(e: MessageEvent) {
    if (e.origin !== parentOrigin) {
      return;
    }

    if (!!e.data.audience && !!audience && e.data.audience !== audience) {
      return;
    }

    if (
      !!e.data.entryPoint &&
      !!entryPoint &&
      e.data.entryPoint !== entryPoint
    ) {
      return;
    }

    const message = readMessageFromParent(e.data);
    if (message) {
      // use slice() to get a clone of the listeners so we're safe if any of the
      // listeners modifies the listener list
      listeners.slice().forEach((l) => l(message));
    }
  }

  function addListener(listener: ParentMessageListenerFn) {
    // https://go/exceptions/EXCEPTION-6405
    // nosemgrep: ban-message-addeventlistener
    window.addEventListener('message', handler);
    listeners.push(listener);
  }

  function removeListener(listener: ParentMessageListenerFn) {
    const index = listeners.findIndex((l) => l === listener);
    if (index >= 0) {
      listeners.splice(index, 1);
    }
    window.removeEventListener('message', handler);
  }

  function post(message: MessageToParent) {
    if (window.parent && window.parent !== window) {
      if (parentOrigin) {
        const wrapped = {
          ...message,
          audience,
          entryPoint,
        };
        devLog('Posting to parent', {message: wrapped, parentOrigin});
        // https://go/exceptions/EXCEPTION-6405
        // nosemgrep: ban-postmessage-calls
        window.parent.postMessage(wrapped, parentOrigin);
      } else {
        devLog(
          'parentOrigin is not set; if you are not running in Storybook this might be a problem',
          {
            message,
            parentOrigin,
          },
        );
      }
    } else {
      devLog('No parent window; are you not iframed?', {
        message,
        parentOrigin,
      });
    }
  }

  function request(
    message: MessageToParent,
    expectedResponseType: MessageFromParentType,
  ) {
    const requestTimeoutInMilliseconds = 500;

    return new Promise<MessageFromParent>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      function requestHandler(fromParent: MessageFromParent) {
        if (fromParent.type === expectedResponseType) {
          clearTimeout(timeoutId);
          removeListener(requestHandler);
          resolve(fromParent);
        }
      }

      timeoutId = setTimeout(() => {
        removeListener(requestHandler);
        reject(new Error('Timed out waiting for a response'));
      }, requestTimeoutInMilliseconds);

      addListener(requestHandler);
      post(message);
    });
  }

  async function requestAnalyticsEventId(): Promise<string | null> {
    try {
      const result = await request(
        {
          type: MessageToParentType.RequestAnalyticsEventId,
        },
        MessageFromParentType.AnalyticsEventId,
      );
      return (result as AnalyticsEventIdMessage).data.analyticsEventId;
    } catch {
      return null;
    }
  }

  return {
    post,
    request,
    requestAnalyticsEventId,
    addListener,
    removeListener,
  };
}
